import React, { useState, useRef, useEffect } from "react";
import TenantDocuments from "./TenantDocuments";
import { useParams } from "react-router-dom";
import {
  FaFileAlt,
  FaExclamationTriangle,
  FaFileUpload,
  FaFileDownload,
  FaFileContract,
  FaIdCard,
  FaReceipt,
  FaFile,
} from "react-icons/fa";
import {
  IoCloudUploadOutline,
  IoClose,
  IoTrashOutline,
  IoDocumentTextOutline,
  IoAddCircleOutline,
  IoSearchOutline,
} from "react-icons/io5";
import {
  getDocDataSnap,
  setDocData,
  storage,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

export default function DocumentsTenants({ tenant }) {
  const { id } = useParams();
  const [documentType, setDocumentType] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [documentDescription, setDocumentDescription] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchTenantDocuments = async () => {
      getDocDataSnap("tenants", id, (tenantData) => {
        // Initialize documents array if it doesn't exist
        const tenantDocs = tenantData.documents || [];
        setDocuments(tenantDocs);
        setIsLoading(false);
      });
    };

    fetchTenantDocuments();
  }, [id]);

  const resetForm = () => {
    setDocumentType("");
    setDocumentName("");
    setDocumentDescription("");
    setUploadProgress(0);
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!documentType || !documentName) {
      alert("Please provide document type and name");
      return;
    }

    setUploadingDoc(true);
    setUploadProgress(0);

    try {
      // Create a safe filename
      const timestamp = new Date().getTime();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, "_");
      const storagePath = `tenants/${id}/documents/${timestamp}_${safeFileName}`;
      const storageRef = ref(storage, storagePath);

      // Create uploadTask
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Listen for state changes
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Track progress
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(progress);
        },
        (error) => {
          // Handle error
          console.error("Upload error:", error);
          setUploadingDoc(false);
          alert("Error uploading document: " + error.message);
        },
        async () => {
          // Upload completed successfully
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Prepare document object
            const newDocument = {
              id: `doc_${timestamp}`,
              name: documentName,
              type: documentType,
              description: documentDescription || "",
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              uploadDate: new Date().toISOString(),
              url: downloadURL,
              path: storagePath,
            };

            console.log("Document object prepared:", newDocument);

            // Make sure documents is initialized as an array
            const currentDocuments = Array.isArray(documents)
              ? [...documents]
              : [];

            // Update tenant's documents in Firestore
            const updatedDocuments = [...currentDocuments, newDocument];

            console.log(
              "Updating Firestore with documents array:",
              updatedDocuments
            );

            // Update Firestore
            await setDocData("tenants", id, {
              documents: updatedDocuments,
            });

            console.log("Firestore update successful");

            // Update local state
            setDocuments(updatedDocuments);

            // Reset form
            resetForm();
          } catch (error) {
            console.error("Error saving document metadata:", error);
            alert(
              "Error saving document information: " +
                (error.message || "Unknown error")
            );
          }

          setUploadingDoc(false);
        }
      );
    } catch (error) {
      console.error("Error in document upload process:", error);
      setUploadingDoc(false);
      alert("Error processing document: " + (error.message || "Unknown error"));
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      // Find and remove the document
      const updatedDocuments = documents.filter((doc) => doc.id !== documentId);

      // Update Firestore
      await setDocData("tenants", id, {
        documents: updatedDocuments,
      });

      // Update local state
      setDocuments(updatedDocuments);
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("Error deleting document. Please try again.");
    }
  };

  const getDocumentIcon = (type, fileType) => {
    if (type === "Lease Contract") {
      return <FaFileContract className="w-4 h-4 text-blue-500" />;
    } else if (type === "ID" || type === "Identification") {
      return <FaIdCard className="w-4 h-4 text-green-500" />;
    } else if (type === "Receipt" || type === "Payment") {
      return <FaReceipt className="w-4 h-4 text-yellow-600" />;
    } else if (fileType && fileType.includes("pdf")) {
      return <FaFileAlt className="w-4 h-4 text-red-500" />;
    } else if (fileType && fileType.includes("image")) {
      return <IoDocumentTextOutline className="w-4 h-4 text-purple-500" />;
    } else {
      return <FaFile className="w-4 h-4 text-gray-500" />;
    }
  };

  // Ensure documents is always an array before filtering
  const filteredDocuments = Array.isArray(documents)
    ? documents.filter(
        (doc) =>
          doc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (doc.description &&
            doc.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-2">
        <div>
          <h2 className="text-sm font-medium text-[#363062]">Documentation</h2>
          <p className="text-xs text-gray-500">Tenant Records & Compliance</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <IoSearchOutline className="h-3.5 w-3.5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-9 pr-3 py-1.5 border border-gray-200 rounded-md focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062] placeholder-gray-400 text-xs"
          placeholder="Search documents by name or type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column - Document Uploader */}
        <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
            <div className="flex items-center gap-1.5">
              <FaFileUpload className="w-3.5 h-3.5 text-[#363062]" />
              <h3 className="text-xs font-medium text-[#363062]">
                Upload Document
              </h3>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Document Type*
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-md p-1.5 focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
                required
              >
                <option value="">Select a type</option>
                <option value="Lease Contract">Lease Contract</option>
                <option value="Lease Amendment">Lease Amendment</option>
                <option value="ID">Tenant ID</option>
                <option value="Background Check">Background Check</option>
                <option value="Payment Receipt">Payment Receipt</option>
                <option value="Inspection Report">Inspection Report</option>
                <option value="Maintenance Request">Maintenance Request</option>
                <option value="Notice">Notice</option>
                <option value="Insurance">Insurance</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Document Name*
              </label>
              <input
                type="text"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="Enter document name"
                className="w-full text-xs border border-gray-200 rounded-md p-1.5 focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={documentDescription}
                onChange={(e) => setDocumentDescription(e.target.value)}
                placeholder="Add a brief description"
                rows="3"
                className="w-full text-xs border border-gray-200 rounded-md p-1.5 focus:ring-1 focus:ring-[#363062]/20 focus:border-[#363062]"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Select File*
              </label>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleDocumentUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              <button
                onClick={() => fileInputRef.current.click()}
                disabled={uploadingDoc || !documentType || !documentName}
                className={`w-full text-xs px-3 py-2 rounded-md flex items-center justify-center ${
                  uploadingDoc || !documentType || !documentName
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-[#363062] text-white hover:bg-[#363062]/90"
                }`}
              >
                <IoCloudUploadOutline className="w-3.5 h-3.5 mr-1.5" />
                {uploadingDoc ? "Uploading..." : "Upload Document"}
              </button>
            </div>

            {uploadingDoc && (
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-[#8FCD00] h-1.5 rounded-full"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-right text-xs text-gray-500 mt-1">
                  {uploadProgress}%
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Document List */}
        <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
            <div className="flex items-center gap-1.5">
              <FaFileAlt className="w-3.5 h-3.5 text-[#363062]" />
              <h3 className="text-xs font-medium text-[#363062]">
                Tenant Documents
              </h3>
            </div>
            {documents.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-[#363062]/70 cursor-pointer hover:text-[#363062]">
                <FaFileDownload className="w-3 h-3" />
                <span>Export All</span>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center p-6">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
            </div>
          ) : documents.length > 0 ? (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="bg-white border border-gray-200 rounded-md p-3 flex justify-between items-center hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      {getDocumentIcon(doc.type, doc.fileType)}
                      <div>
                        <h4 className="text-xs font-medium text-gray-800">
                          {doc.name}
                        </h4>
                        <p className="text-[10px] text-gray-500">
                          {doc.type} •{" "}
                          {new Date(doc.uploadDate).toLocaleDateString()}
                        </p>
                        {doc.description && (
                          <p className="text-[10px] text-gray-600 mt-1">
                            {doc.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] bg-blue-50 text-blue-600 px-2 py-1 rounded flex items-center"
                      >
                        <FaFileDownload className="w-2.5 h-2.5 mr-1" />
                        View
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded flex items-center"
                      >
                        <IoTrashOutline className="w-2.5 h-2.5 mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-500">
                    No documents match your search criteria
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 bg-gray-50 rounded-md h-full">
              <div className="w-12 h-12 mb-3 rounded-full bg-[#363062]/5 flex items-center justify-center">
                <IoDocumentTextOutline className="w-5 h-5 text-[#363062]/30" />
              </div>
              <p className="text-xs text-gray-500 mb-1">
                No documents uploaded
              </p>
              <p className="text-[10px] text-gray-400 mb-2">
                Upload lease agreements, ID documents and other tenant records
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Violations Section */}
      <div className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <FaExclamationTriangle className="w-3 h-3 text-[#363062]" />
            <h3 className="text-xs font-medium text-[#363062]">
              Violations and Penalties
            </h3>
          </div>
          <div className="text-xs text-gray-400">Last updated: Never</div>
        </div>

        <div className="p-3 bg-gray-50 rounded-md text-center">
          <div className="flex flex-col items-center justify-center py-3">
            <div className="w-10 h-10 mb-2 rounded-full bg-[#363062]/5 flex items-center justify-center">
              <FaExclamationTriangle className="w-4 h-4 text-[#363062]/30" />
            </div>
            <p className="text-xs text-gray-500">No Violations and Penalties</p>
            <p className="text-[10px] text-gray-400 mt-1">
              Tenant is in good standing
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex items-center justify-end gap-2 mt-2">
        <button className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-[#363062] bg-[#363062]/5 rounded-md hover:bg-[#363062]/10 transition-colors duration-200">
          View History
        </button>
        <button className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-[#363062] bg-[#363062]/5 rounded-md hover:bg-[#363062]/10 transition-colors duration-200">
          Generate Report
        </button>
      </div>
    </div>
  );
}
